const EXPORT_DATE_MIN_MS = Date.UTC(2000, 0, 1);
const EXPORT_DATE_MAX_MS = Date.UTC(2100, 0, 1);

/**
 * Calendar dates for Excel/HTML export: day.month.twoDigitYear (e.g. 13.5.26).
 * Parses ISO date/datetime strings and Unix timestamps (ms or s); returns null if not date-like.
 */
export function formatExportDateDMYY(value: unknown): string | null {
    if (value === null || value === undefined) return null;

    if (typeof value === 'string') {
        const t = value.trim();
        if (!t) return null;
        const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
        if (ymd) {
            const y = parseInt(ymd[1], 10);
            const mo = parseInt(ymd[2], 10);
            const d = parseInt(ymd[3], 10);
            if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
                return `${d}.${mo}.${y % 100}`;
            }
        }
        if (/^\d{4}-\d{2}-\d{2}T/.test(t) || t.includes('Z') || /[+-]\d{2}:?\d{2}$/.test(t)) {
            const parsed = new Date(t);
            if (!Number.isNaN(parsed.getTime())) {
                const ms = parsed.getTime();
                if (ms >= EXPORT_DATE_MIN_MS && ms < EXPORT_DATE_MAX_MS) {
                    return `${parsed.getDate()}.${parsed.getMonth() + 1}.${parsed.getFullYear() % 100}`;
                }
            }
        }
        if (/^\d{10,13}$/.test(t)) {
            const num = Number(t);
            const ms = num > 1e12 ? num : num * 1000;
            if (ms >= EXPORT_DATE_MIN_MS && ms < EXPORT_DATE_MAX_MS) {
                const parsed = new Date(ms);
                if (!Number.isNaN(parsed.getTime())) {
                    return `${parsed.getDate()}.${parsed.getMonth() + 1}.${parsed.getFullYear() % 100}`;
                }
            }
        }
        return null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        const ms = value > 1e12 ? value : value * 1000;
        if (ms >= EXPORT_DATE_MIN_MS && ms < EXPORT_DATE_MAX_MS) {
            const parsed = new Date(ms);
            if (!Number.isNaN(parsed.getTime())) {
                return `${parsed.getDate()}.${parsed.getMonth() + 1}.${parsed.getFullYear() % 100}`;
            }
        }
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const ms = value.getTime();
        if (ms >= EXPORT_DATE_MIN_MS && ms < EXPORT_DATE_MAX_MS) {
            return `${value.getDate()}.${value.getMonth() + 1}.${value.getFullYear() % 100}`;
        }
    }

    return null;
}

function formatExportCellValue<T>(obj: T, key: keyof T, localization?: Record<string, string>): string {
    const raw = obj[key];
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'string' && raw.trim() === '') return '';

    const asDate = formatExportDateDMYY(raw);
    if (asDate !== null) return asDate;

    const locKey = String(raw);
    const localized = localization?.[locKey];
    if (localized !== undefined && localized !== null && String(localized) !== '') {
        return String(localized);
    }

    if (!raw && raw !== 0 && raw !== false) return '';

    return String(raw);
}

export const arrayToExcel = (() => {
    //STEP 2: Append Table data to Spreadsheet XML Template.
    const createXMLTable = (table: string, fileName: string) => {
        const xmlTable = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40"
        >
           <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8"/>
           <head>
              <xml>
                <x:ExcelWorkbook>
                    <x:ExcelWorksheets>
                        <x:ExcelWorksheet>
                            <x:Name>${fileName}</x:Name>
                            <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                        </x:ExcelWorksheet>
                    </x:ExcelWorksheets>
                </x:ExcelWorkbook>
              </xml>
           </head>
           <body>
             ${table}
           </body>
        </html> `;
        return xmlTable;
    };

    //STEP 3: Create fileURL from XML template for download
    const createFileUrl = (xmlTable: string) => {
        const tableBlob = new Blob([xmlTable], { type: 'application/vnd.ms-excel;base64,' });
        const downloadURL = URL.createObjectURL(tableBlob);
        return downloadURL;
    };

    //STEP 5: Create download link on button click
    const downloadFile = (downloadURL: string, fileName: string) => {
        const downloadLink = document.createElement('a');
        document.body.appendChild(downloadLink);
        downloadLink.download = fileName;
        downloadLink.href = downloadURL;
        downloadLink.click();
    };

    //STEP 1: Convert Array to HTML Table.
    return {
        convertArrayToTable: async <T>(array: Array<T>, fileName: string, localization?: any) => {
            //use keys from the first array object to form table column headers
            const tableHeaders = `<tr style="background-color: #008ac9; color: #fff; font-size: 18px">${Object.keys(array[0])
                .map((key) => `<td>${localization[key] || key}</td>`)
                .join('')}</tr>`;
            //now loop through all array objects to form table rows
            const tableRows = array
                .map((obj) => [
                    `<tr>${(Object.keys(obj) as Array<keyof T>)
                        .map((key) => `<td>${formatExportCellValue(obj, key, localization)}</td>`)
                        .join('')}</tr>`
                ])
                .join('');

            const table = `<table>${tableHeaders}${tableRows}</table>`.trim();

            const xmlTable = createXMLTable(table, fileName);
            const downloadURL = createFileUrl(xmlTable);
            downloadFile(downloadURL, fileName);
        }
    };
})();
